import React from "react";
import { FiCheck, FiCopy } from "react-icons/fi";

export const naira = (n) => `₦${Number(n).toLocaleString()}`;
export const titleCase = (v) =>
  /^\d+$/.test(String(v)) ? String(v) : String(v).replace(/\b\w/g, (c) => c.toUpperCase());
export const groupToken = (t) => String(t).replace(/\D/g, "").replace(/(\d{4})(?=\d)/g, "$1 ");

export function CopyButton({ value, label = "Copy" }) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(String(value)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition ${
        copied ? "bg-green-100 text-green-700" : "bg-primary-dark text-white hover:bg-primary-light"
      }`}
    >
      {copied ? <FiCheck /> : <FiCopy />}
      {copied ? "Copied" : label}
    </button>
  );
}

// Coloured message box: red = failed/error, green = success, amber = waiting, blue = processing.
export function Notice({ tone, icon: Icon, children, title }) {
  const tones = {
    red: "bg-red-50 border-red-300 text-red-800",
    green: "bg-green-50 border-green-300 text-green-800",
    amber: "bg-amber-50 border-amber-300 text-amber-900",
    blue: "bg-sky-50 border-sky-300 text-sky-900",
  };
  return (
    <div role="status" className={`flex gap-3 rounded-2xl border px-4 py-3 text-left ${tones[tone]}`}>
      {Icon && <Icon className="mt-0.5 shrink-0 text-lg" />}
      <div className="min-w-0 text-[15px] leading-snug">
        {title && <p className="font-bold">{title}</p>}
        <div className="font-medium">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, error, prefix, ...props }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-semibold text-primary-dark">{label}</label>
      <div
        className={`flex items-center rounded-xl bg-[#F3F3F7] ring-2 transition ${
          error ? "ring-red-400" : "ring-transparent focus-within:ring-primary-light/40"
        }`}
      >
        {prefix && <span className="pl-4 font-semibold text-[#6C6F7F]">{prefix}</span>}
        <input
          {...props}
          className="w-full bg-transparent px-4 py-3 text-base outline-none placeholder:text-[#9AA0B4]"
        />
      </div>
      {error && <span className="text-sm font-medium text-red-600">{error}</span>}
    </div>
  );
}

export const PrimaryButton = ({ children, loading, type = "button", ...props }) => (
  <button
    type={type}
    {...props}
    className="flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-primary-dark text-base font-semibold text-white transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-40"
  >
    {loading && (
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
    )}
    {children}
  </button>
);


export function SelectField({ label, error, children, ...props }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-semibold text-primary-dark">{label}</label>
      <div
        className={`relative rounded-xl bg-[#F3F3F7] ring-2 transition ${
          error ? "ring-red-400" : "ring-transparent focus-within:ring-primary-light/40"
        }`}
      >
        <select
          {...props}
          className="w-full appearance-none bg-transparent px-4 py-3 pr-10 text-base outline-none"
        >
          {children}
        </select>
        <svg
          aria-hidden
          viewBox="0 0 20 20"
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6C6F7F]"
          fill="currentColor"
        >
          <path d="M5.5 7.5l4.5 4.5 4.5-4.5z" />
        </svg>
      </div>
      {error && <span className="text-sm font-medium text-red-600">{error}</span>}
    </div>
  );
}
